import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Button, Card, Spinner } from '@timely/ui';
import { useSession } from './SessionProvider';

/**
 * Nothing behind this renders until we know who is asking.
 *
 * The three states are kept apart on purpose:
 *
 *   loading  — we do not know yet, so we render neither the screen nor the
 *              sign-in page. Guessing wrong in either direction is a flash of
 *              the wrong thing, and in one direction it is a flash of somebody
 *              else's data.
 *   expired  — a session that ran out. This is what a tab left open all day
 *              actually reaches, and it deserves a sentence rather than a
 *              silent bounce to a login form that looks like a bug.
 *   signed-out — off to sign in, remembering where they were going.
 */
export function RequireAuth() {
    const { state, loading, signOut, openedByLink } = useSession();
    const location = useLocation();
    // Set when this guard ended a link-opened session, so the sign-in page can say why.
    const endedLink = useRef(false);

    if (loading) {
        return (
            <div className="min-h-dvh grid place-items-center" role="status" aria-live="polite">
                <span className="sr-only">Checking your session</span>
                <Spinner className="text-accent-text w-6 h-6" />
            </div>
        );
    }

    if (state === 'expired') {
        return (
            <main className="min-h-dvh grid place-items-center px-4">
                <Card className="max-w-md w-full px-7 py-8 text-center" role="alert">
                    <h1 className="text-title font-semibold text-label tracking-heading">
                        Your session has expired
                    </h1>
                    <p className="text-body text-label-2 mt-3 leading-relaxed">
                        You were signed out after a period of inactivity. Anything you had saved is
                        safe. Sign in again to carry on where you were.
                    </p>
                    <div className="mt-7">
                        <Button variant="primary" onClick={() => { void signOut(); }}>
                            Sign in again
                        </Button>
                    </div>
                </Card>
            </main>
        );
    }

    if (state === 'signed-out') {
        const returnTo = `${location.pathname}${location.search}`;
        return <Navigate to={`/login?${endedLink.current ? 'again=1&' : ''}returnTo=${encodeURIComponent(returnTo)}`} replace />;
    }

    // A session an emailed link opened may set a new password without the current one (Auth's rule
    // for reset links). It has no business anywhere but the reset screen: ended here, before anything
    // renders, and the person signs in with their password (docs/product/phase-0/03 §2).
    if (openedByLink) {
        endedLink.current = true;
        return <EndLinkSession />;
    }

    return <Outlet />;
}

/**
 * Ends a link-opened session in this browser only, and shows nothing while it
 * does. The screen that rendered it sends the person on once they are signed out.
 */
export function EndLinkSession() {
    const { state, endThisSession } = useSession();
    useEffect(() => {
        if (state === 'active') void endThisSession();
    }, [state, endThisSession]);
    return (
        <div className="min-h-dvh grid place-items-center" role="status" aria-live="polite">
            <span className="sr-only">Signing you out of the emailed link</span>
            <Spinner className="text-accent-text w-6 h-6" />
        </div>
    );
}

/**
 * Past this point the address has been proved.
 *
 * `create_workspace()` and `accept_invite()` both refuse an unverified account
 * in the database, so this is not the boundary — it is the screen that explains
 * the refusal before somebody runs into it.
 */
export function RequireVerified() {
    const { verified } = useSession();
    if (!verified) return <Navigate to="/verify-email" replace />;
    return <Outlet />;
}
